import { Hitbox, LetterDef } from '@core/tellent-skills-types';

import {
  CLOUD_COLOR,
  CLOUD_PATH,
  GRASS_PATH,
  GRAVITY,
  JUMP_VELOCITY,
  LETTER_DEFS,
  LION_COLOR,
  OBSTACLE_SCALE,
  STAR_COLOR,
  STAR_PATH,
  STAR_SCALE,
  TELLENT_COLOR,
} from './constants';

export class Lion {
  public width = 46;
  public height = 36;
  public x = 0;
  public y = 0;
  public isJumping = false;
  private vy = 0;

  public jump(): void {
    if (!this.isJumping) {
      this.vy = JUMP_VELOCITY;
      this.isJumping = true;
    }
  }

  public update(groundY: number): void {
    this.vy += GRAVITY;
    this.y += this.vy;
    if (this.y > groundY - this.height) {
      this.y = groundY - this.height;
      this.vy = 0;
      this.isJumping = false;
    }
  }

  public draw(ctx: CanvasRenderingContext2D, frameCount: number, isPlaying: boolean): void {
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.fillStyle = LION_COLOR;
    ctx.fillRect(10, 16, 24, 12);
    ctx.fillRect(24, 6, 16, 18);
    ctx.fillRect(30, 10, 14, 10);
    ctx.fillRect(40, 14, 6, 6);

    const isWalking = isPlaying && !this.isJumping;
    const animState = Math.floor(frameCount / 8) % 2;

    if (this.isJumping) {
      ctx.fillRect(8, 26, 4, 8);
      ctx.fillRect(16, 28, 4, 8);
      ctx.fillRect(30, 28, 4, 8);
      ctx.fillRect(38, 26, 4, 8);
    } else if (isWalking) {
      if (animState === 0) {
        ctx.fillRect(12, 28, 4, 8);
        ctx.fillRect(18, 26, 4, 8);
        ctx.fillRect(28, 26, 4, 8);
        ctx.fillRect(34, 28, 4, 8);
      } else {
        ctx.fillRect(12, 26, 4, 8);
        ctx.fillRect(18, 28, 4, 8);
        ctx.fillRect(28, 28, 4, 8);
        ctx.fillRect(34, 26, 4, 8);
      }
    } else {
      ctx.fillRect(12, 28, 4, 8);
      ctx.fillRect(18, 28, 4, 8);
      ctx.fillRect(28, 28, 4, 8);
      ctx.fillRect(34, 28, 4, 8);
    }

    ctx.fillRect(2, 16, 8, 4);
    const tailOffset = Math.sin(Date.now() / 200) * 2;
    ctx.fillRect(0, 12 + tailOffset, 4, 8);

    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = 'rgba(0,0,0,1)';
    ctx.fillRect(38, 12, 4, 4);
    ctx.globalCompositeOperation = 'source-over';

    ctx.restore();
  }

  public getHitbox(): Hitbox {
    return { x: this.x + 8, y: this.y + 6, width: this.width - 16, height: this.height - 10 };
  }
}

export class Obstacle {
  private readonly letterDef: LetterDef;
  private readonly path2d: Path2D;
  public readonly width: number;
  public readonly height: number;
  public x: number;
  public y: number;
  public isDestroyed = false;
  private vy = 0;
  private vx = 0;
  private rotation = 0;
  private rotSpeed = 0;

  constructor(x: number, groundY: number, letterIndex: number) {
    this.x = x;
    this.letterDef = LETTER_DEFS[letterIndex];
    this.path2d = new Path2D(this.letterDef.path);
    this.width = this.letterDef.width * OBSTACLE_SCALE;
    this.height = 22 * OBSTACLE_SCALE;
    this.y = groundY - this.height;
  }

  public destroy(): void {
    this.isDestroyed = true;
    this.vy = -7;
    this.vx = 2;
    this.rotSpeed = (Math.random() > 0.5 ? 1 : -1) * 0.15;
  }

  public update(gameSpeed: number): void {
    if (this.isDestroyed) {
      this.vy += GRAVITY;
      this.y += this.vy;
      this.x += this.vx - gameSpeed;
      this.rotation += this.rotSpeed;
    } else {
      this.x -= gameSpeed;
    }
  }

  public draw(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    if (this.isDestroyed) {
      const centerX = this.x + this.width / 2;
      const centerY = this.y + this.height / 2;
      ctx.translate(centerX, centerY);
      ctx.rotate(this.rotation);
      ctx.translate(-centerX, -centerY);
    }
    ctx.translate(this.x - this.letterDef.offsetX * OBSTACLE_SCALE, this.y);
    ctx.scale(OBSTACLE_SCALE, OBSTACLE_SCALE);
    ctx.fillStyle = TELLENT_COLOR;
    ctx.fill(this.path2d);
    ctx.restore();
  }

  public getHitbox(): Hitbox {
    return { x: this.x + 4, y: this.y + 4, width: this.width - 8, height: this.height - 4 };
  }

  public getRight(): number {
    return this.x + this.width;
  }
}

export class Star {
  private readonly size = 18 * STAR_SCALE;
  public readonly y: number;
  public gathered = false;
  public x: number;

  constructor(x: number, groundY: number) {
    this.x = x;
    this.y = groundY - 20 - Math.random() * 60;
  }

  public update(gameSpeed: number): void {
    this.x -= gameSpeed;
  }

  public draw(ctx: CanvasRenderingContext2D): void {
    if (this.gathered) return;
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.scale(STAR_SCALE, STAR_SCALE);
    ctx.fillStyle = STAR_COLOR;
    ctx.fill(STAR_PATH);
    ctx.restore();
  }

  public getHitbox(): Hitbox {
    return { x: this.x + 2, y: this.y + 2, width: this.size - 4, height: this.size - 4 };
  }

  public getRight(): number {
    return this.x + this.size;
  }
}

export class Cloud {
  private readonly scale: number;
  private readonly width: number;
  private readonly baseSpeed: number;
  public readonly y: number;
  public x: number;

  constructor(x: number, groundY: number) {
    this.x = x;
    this.scale = 0.5 + Math.random() * 0.7;
    this.width = 100 * this.scale;
    this.y = 10 + Math.random() * (groundY * 0.5);
    this.baseSpeed = 0.2 + Math.random() * 0.3;
  }

  public update(isPlaying: boolean, gameSpeed: number): void {
    this.x -= this.baseSpeed + (isPlaying ? gameSpeed * 0.15 : 0);
  }

  public draw(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.scale(this.scale, this.scale);
    ctx.fillStyle = CLOUD_COLOR;
    ctx.fill(CLOUD_PATH);
    ctx.restore();
  }

  public getRight(): number {
    return this.x + this.width;
  }
}

export class GrassTuft {
  private readonly y: number;
  private readonly scale: number;
  private readonly width: number;
  public x: number;

  constructor(x: number, groundY: number) {
    this.x = x;
    this.scale = 0.8 + Math.random() * 0.4;
    this.width = 10 * this.scale;
    this.y = groundY - 10 * this.scale + 1;
  }

  public update(gameSpeed: number): void {
    this.x -= gameSpeed;
  }

  public draw(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.scale(this.scale, this.scale);
    ctx.fillStyle = CLOUD_COLOR;
    ctx.fill(GRASS_PATH);
    ctx.restore();
  }

  public getRight(): number {
    return this.x + this.width;
  }
}

export function checkCollision(a: Hitbox, b: Hitbox): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}
